import React, { useContext, useEffect, useMemo } from "react";
import Box from "@material-ui/core/Box";
import { Skeleton } from "@material-ui/lab";
import { Switch, Route, useLocation } from "react-router-dom";

import {
  PRICE_DROP_PROTECTION_BODY,
  PRICE_PREDICTION_PRICE_WATCH_BODY,
  BEST_PRICE_GUARANTEE_BODY,
  FLIGHT_CFAR_TERMS_BODY,
  FLIGHT_CHFAR_TERMS_BODY,
  HOTEL_CFAR_TERMS_BODY,
  HOTEL_CFAR_TERMS_TABLE,
  DISRUPTION_PROTECTION_BODY,
  TRAVEL_CREDITS_BODY,
  HOTEL_PRICE_DROP_PROTECTION_BODY,
  MISSED_CONNECTION_GUARANTEE_BODY,
  MISSED_CONNECTION_GUARANTEE_TITLE,
  MISSED_CONNECTION_GUARANTEE_SUBTITLE,
} from "redmond";
import { ContentBlock } from "halifax";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  getConfigTenant,
  getPortalName,
  PATH_TERMS_COMPLIMENTARY_NIGHT,
  PATH_TERMS_LUXURY_OFFER,
  PATH_TERMS_LUX_Q2_VR_OFFER,
  travelAppInterface,
  TravelAppInterfaceEventName,
} from "@capone/common";
import { IContentBlockProps } from "halifax/build/ContentBlock/component";
import {
  PATH_PRICE_PREDICTION_PRICE_WATCH,
  PATH_PRICE_DROP_PROTECTION,
  PATH_CANCEL_FOR_ANY_REASON,
  PATH_CHANGE_FOR_ANY_REASON,
  PATH_HOTEL_CANCEL_FOR_ANY_REASON,
  PATH_DISRUPTION_PROTECTION,
  PATH_TRAVEL_OFFERS,
  PATH_TRAVEL_CREDITS,
  PATH_BEST_PRICE_GUARANTEE,
  PATH_SITE_TERMS_OF_USE,
  PATH_TERMS_OF_SERVICE,
  PATH_HOME,
  PATH_PREMIER_COLLECTION,
  PATH_HOTEL_PRICE_DROP,
  PATH_MISSED_CONNECTION_GUARANTEE,
} from "../../../utils/paths";

import {
  BEST_PRICE_GUARANTEE_TITLE,
  BEST_PRICE_GUARANTEE_SUBTITLE,
} from "./TextConstants/bestPriceGuaranteeTextConstant";

import {
  CANCEL_FOR_ANY_REASON_TITLE,
  CANCEL_FOR_ANY_REASON_SUBTITLE,
  HOTEL_CANCEL_FOR_ANY_REASON_TITLE,
  HOTEL_CANCEL_FOR_ANY_REASON_SUBTITLE,
} from "./TextConstants/cancelForAnyReasonTextConstant";

import {
  PRICE_DROP_PROTECTION_TITLE,
  PRICE_DROP_PROTECTION_SUBTITLE,
  HOTEL_PRICE_DROP_PROTECTION_TITLE,
} from "./TextConstants/priceDropProtectionTextConstant";

import {
  TERMS_OF_SERVICE_TITLE,
  TERMS_OF_SERVICE_SUBTITLE,
  TERMS_OF_SERVICE_CANCEL_FOR_ANY_REASON_SUBTITLE,
  TERMS_OF_SERVICE_CHANGE_FOR_ANY_REASON_SUBTITLE,
  TERMS_OF_SERVICE_HOTEL_CANCEL_FOR_ANY_REASON_SUBTITLE,
  TERMS_OF_SERVICE_BODY,
  TERMS_OF_SERVICE_DISRUPTION_PROTECTION_SUBTITLE,
  TERMS_OF_SERVICE_TRAVEL_OFFERS_SUBTITLE,
  TERMS_OF_SERVICE_TRAVEL_CREDITS_SUBTITLE,
  TERMS_OF_SERVICE_PREMIER_COLLECTION_SUBTITLE,
  TERMS_OF_SERVICE_LIFESTYLE_COLLECTION_SUBTITLE,
  TERMS_OF_SERVICE_TREES_SUBTITLE,
  TREES_BODY,
  TERMS_OF_SERVICE_HOTEL_PRICE_DROP_PROTECTION_SUBTITLE,
  TERMS_OF_SERVICE_PRICE_MATCH_GUARANTEE,
  TERMS_OF_SERVICE_SELLER_OF_TRAVEL_DISCLOSURE,
  TERMS_OF_SERVICE_MISSED_CONNECTION_GUARANTEE_TITLE,
} from "./TextConstants/termsOfServiceTextConstant";
import {
  SITE_TERMS_OF_USE_TITLE,
  SITE_TERMS_OF_USE_SUBTITLE,
  SITE_TERMS_OF_USE_BODY,
} from "./TextConstants/siteTermOfUseTextConstant";

import {
  PRICE_PREDICTION_PRICE_WATCH_TITLE,
  PRICE_PREDICTION_PRICE_WATCH_SUBTITLE,
} from "./TextConstants/pricePredictionPriceWatchTextConstants";
import {
  DISRUPTION_PROTECTION_SUBTITLE,
  DISRUPTION_PROTECTION_TITLE,
} from "./TextConstants/disruptionProtectionTextConstant";
import {
  TRAVEL_OFFERS_TITLE,
  TRAVEL_OFFERS_SUBTITLE,
  TRAVEL_OFFERS_BODY,
  TRAVEL_OFFERS_FOOTER,
} from "./TextConstants/travelOffersTextContant";
import {
  TRAVEL_CREDITS_TITLE,
  TRAVEL_CREDITS_SUBTITLE,
} from "./TextConstants/travelCreditsTextContant";

import {
  PREMIER_COLLECTION_BODY,
  PREMIER_COLLECTION_SUBTITLE,
  PREMIER_COLLECTION_TITLE,
} from "./TextConstants/premierCollectionTextConstants";

import {
  LIFESTYLE_COLLECTION_BODY,
  LIFESTYLE_COLLECTION_FOOTER,
} from "./TextConstants/lifestyleCollectionTextConstants";

import {
  AVAILABLE,
  getExperimentVariant,
  getExperimentVariantJSON,
  LUX_Q2_VR_OFFER_CTAS,
  LuxQ2VROfferCtasConfig,
  PREMIER_COLLECTION_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  HOTEL_PRICE_DROP_SMOKE_TEST,
  useExperiments,
  HOTEL_PRICE_DROP_SMOKE_TEST_25_DOLLAR,
  HOTEL_PRICE_DROP_SMOKE_TEST_FORCE_25_DOLLAR,
  getExperimentVariantCustomVariants,
  HOTEL_PRICE_DROP_SMOKE_TEST_VARIANTS,
  HOTEL_PRICE_DROP_SMOKE_TEST_10_DOLLAR,
  HOTEL_PRICE_DROP_SMOKE_TEST_50_DOLLAR,
} from "../../../context/experiments";
import { ClientContext } from "../../../App";

import { CHANGE_FOR_ANY_REASON_TITLE } from "./TextConstants/changeForAnyReasonTextConstant";
import {
  COMPLIMENTARY_NIGHT_BODY,
  COMPLIMENTARY_NIGHT_TITLE,
} from "./TextConstants/complimentaryNightConstant";
import {
  LUXURY_OFFER_BODY,
  LUXURY_OFFER_TITLE,
} from "./TextConstants/luxuryOfferConstants";
const Body = () => {
  const { hash } = useLocation();
  const expState = useExperiments();
  const { travelWalletOffers } = useContext(ClientContext);

  const luxQ2VROfferCtasConfig = getExperimentVariantJSON(
    expState.experiments,
    LUX_Q2_VR_OFFER_CTAS,
  ) as LuxQ2VROfferCtasConfig | null;

  const q2VROffer = useMemo(() => {
    const offerIds = luxQ2VROfferCtasConfig?.offerIds;
    if (!offerIds || offerIds.length === 0 || !travelWalletOffers)
      return undefined;
    return travelWalletOffers.find((offer) =>
      offer.descriptions.some((desc) => offerIds.includes(desc)),
    );
  }, [travelWalletOffers, luxQ2VROfferCtasConfig]);

  const travelWalletOffer = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_OFFER_EXPERIMENT,
  );
  const isTravelWalletOfferExperiment = React.useMemo(
    () => travelWalletOffer === AVAILABLE,
    [travelWalletOffer],
  );

  const premierCollection = getExperimentVariant(
    expState.experiments,
    PREMIER_COLLECTION_EXPERIMENT,
  );
  const isPremierCollectionExperiment = React.useMemo(
    () => premierCollection === AVAILABLE,
    [premierCollection],
  );

  const hotelPriceDropSmokeTestExperiment = getExperimentVariantCustomVariants(
    expState.experiments,
    HOTEL_PRICE_DROP_SMOKE_TEST,
    HOTEL_PRICE_DROP_SMOKE_TEST_VARIANTS,
  );
  const isHotelPriceDropAvailable =
    hotelPriceDropSmokeTestExperiment ===
      HOTEL_PRICE_DROP_SMOKE_TEST_25_DOLLAR ||
    hotelPriceDropSmokeTestExperiment ===
      HOTEL_PRICE_DROP_SMOKE_TEST_FORCE_25_DOLLAR ||
    hotelPriceDropSmokeTestExperiment ===
      HOTEL_PRICE_DROP_SMOKE_TEST_10_DOLLAR ||
    hotelPriceDropSmokeTestExperiment === HOTEL_PRICE_DROP_SMOKE_TEST_50_DOLLAR;

  const getBodyContent = (path: string): IContentBlockProps | null => {
    switch (path) {
      case PATH_PRICE_PREDICTION_PRICE_WATCH:
        return {
          title: PRICE_PREDICTION_PRICE_WATCH_TITLE,
          subtitle: PRICE_PREDICTION_PRICE_WATCH_SUBTITLE,
          body: PRICE_PREDICTION_PRICE_WATCH_BODY(getConfigTenant()),
        };
      case PATH_PRICE_DROP_PROTECTION:
        return {
          title: PRICE_DROP_PROTECTION_TITLE,
          subtitle: PRICE_DROP_PROTECTION_SUBTITLE,
          body: PRICE_DROP_PROTECTION_BODY,
        };
      case PATH_MISSED_CONNECTION_GUARANTEE:
        return {
          title: MISSED_CONNECTION_GUARANTEE_TITLE,
          subtitle: MISSED_CONNECTION_GUARANTEE_SUBTITLE,
          body: MISSED_CONNECTION_GUARANTEE_BODY,
        };
      case PATH_HOTEL_PRICE_DROP:
        if (!isHotelPriceDropAvailable) {
          return null;
        }
        return {
          title: HOTEL_PRICE_DROP_PROTECTION_TITLE,
          subtitle: PRICE_DROP_PROTECTION_SUBTITLE,
          body: HOTEL_PRICE_DROP_PROTECTION_BODY,
        };
      case PATH_BEST_PRICE_GUARANTEE:
        return {
          title: BEST_PRICE_GUARANTEE_TITLE,
          subtitle: BEST_PRICE_GUARANTEE_SUBTITLE,
          body: BEST_PRICE_GUARANTEE_BODY,
        };
      case PATH_CANCEL_FOR_ANY_REASON:
        return {
          title: CANCEL_FOR_ANY_REASON_TITLE,
          subtitle: CANCEL_FOR_ANY_REASON_SUBTITLE,
          body: FLIGHT_CFAR_TERMS_BODY(getPortalName()),
        };
      case PATH_CHANGE_FOR_ANY_REASON:
        return {
          title: CHANGE_FOR_ANY_REASON_TITLE,
          body: FLIGHT_CHFAR_TERMS_BODY(getPortalName()),
        };
      case PATH_HOTEL_CANCEL_FOR_ANY_REASON:
        return {
          title: HOTEL_CANCEL_FOR_ANY_REASON_TITLE,
          subtitle: HOTEL_CANCEL_FOR_ANY_REASON_SUBTITLE,
          body: HOTEL_CFAR_TERMS_BODY(getPortalName()) + HOTEL_CFAR_TERMS_TABLE,
        };
      case PATH_DISRUPTION_PROTECTION:
        return {
          title: DISRUPTION_PROTECTION_TITLE,
          subtitle: DISRUPTION_PROTECTION_SUBTITLE,
          body: DISRUPTION_PROTECTION_BODY,
        };
      case PATH_TRAVEL_OFFERS:
        return {
          title: TRAVEL_OFFERS_TITLE,
          subtitle: TRAVEL_OFFERS_SUBTITLE,
          body: TRAVEL_OFFERS_BODY + TRAVEL_OFFERS_FOOTER,
        };
      case PATH_PREMIER_COLLECTION: {
        // Combine Premier and Lifestyle Collection text
        const premierBody =
          TERMS_OF_SERVICE_PREMIER_COLLECTION_SUBTITLE +
          PREMIER_COLLECTION_BODY +
          TERMS_OF_SERVICE_LIFESTYLE_COLLECTION_SUBTITLE +
          LIFESTYLE_COLLECTION_BODY +
          LIFESTYLE_COLLECTION_FOOTER;
        return {
          title: PREMIER_COLLECTION_TITLE,
          subtitle: PREMIER_COLLECTION_SUBTITLE,
          body: premierBody,
        };
      }
      case PATH_TRAVEL_CREDITS:
        return {
          title: TRAVEL_CREDITS_TITLE,
          subtitle: TRAVEL_CREDITS_SUBTITLE,
          body: TRAVEL_CREDITS_BODY,
        };
      case PATH_TERMS_COMPLIMENTARY_NIGHT:
        return {
          title: COMPLIMENTARY_NIGHT_TITLE,
          body: COMPLIMENTARY_NIGHT_BODY,
        };
      case PATH_TERMS_LUXURY_OFFER:
        return {
          title: LUXURY_OFFER_TITLE,
          body: LUXURY_OFFER_BODY,
        };
      case PATH_TERMS_LUX_Q2_VR_OFFER:
        if (!q2VROffer?.termsAndConditions) return null;
        return {
          title: `${q2VROffer.title} - Terms and Conditions`,
          body: q2VROffer.termsAndConditions,
        };
      case PATH_SITE_TERMS_OF_USE:
        return {
          title: SITE_TERMS_OF_USE_TITLE,
          subtitle: SITE_TERMS_OF_USE_SUBTITLE,
          body: SITE_TERMS_OF_USE_BODY(dayjs().year()),
        };
      case PATH_TERMS_OF_SERVICE:
      default: {
        let body = TERMS_OF_SERVICE_BODY;
        if (isHotelPriceDropAvailable) {
          body +=
            TERMS_OF_SERVICE_HOTEL_PRICE_DROP_PROTECTION_SUBTITLE +
            HOTEL_PRICE_DROP_PROTECTION_BODY;
        }
        body +=
          TERMS_OF_SERVICE_PRICE_MATCH_GUARANTEE + BEST_PRICE_GUARANTEE_BODY;
        body +=
          TERMS_OF_SERVICE_CANCEL_FOR_ANY_REASON_SUBTITLE +
          FLIGHT_CFAR_TERMS_BODY(getPortalName());
        body +=
          TERMS_OF_SERVICE_CHANGE_FOR_ANY_REASON_SUBTITLE +
          FLIGHT_CHFAR_TERMS_BODY(getPortalName());
        body +=
          TERMS_OF_SERVICE_HOTEL_CANCEL_FOR_ANY_REASON_SUBTITLE +
          HOTEL_CFAR_TERMS_BODY(getPortalName()) +
          HOTEL_CFAR_TERMS_TABLE;
        body += TERMS_OF_SERVICE_DISRUPTION_PROTECTION_SUBTITLE;
        body += DISRUPTION_PROTECTION_BODY;
        if (isTravelWalletOfferExperiment) {
          body += TERMS_OF_SERVICE_TRAVEL_OFFERS_SUBTITLE + TRAVEL_OFFERS_BODY;
        }
        body +=
          TERMS_OF_SERVICE_TRAVEL_CREDITS_SUBTITLE +
          TRAVEL_CREDITS_BODY +
          TERMS_OF_SERVICE_TREES_SUBTITLE +
          TREES_BODY;
        body +=
          TERMS_OF_SERVICE_PREMIER_COLLECTION_SUBTITLE +
          PREMIER_COLLECTION_BODY +
          TERMS_OF_SERVICE_LIFESTYLE_COLLECTION_SUBTITLE +
          LIFESTYLE_COLLECTION_BODY;
        body +=
          TERMS_OF_SERVICE_MISSED_CONNECTION_GUARANTEE_TITLE +
          MISSED_CONNECTION_GUARANTEE_BODY;
        body += TERMS_OF_SERVICE_SELLER_OF_TRAVEL_DISCLOSURE;
        return {
          title: TERMS_OF_SERVICE_TITLE,
          subtitle: TERMS_OF_SERVICE_SUBTITLE,
          body,
        };
      }
    }
  };

  // Usually you can scroll an id into view by using it's id as the URL hash.
  // But since terms-module and content loads after initial page load, this will scroll
  // elements with the matching hash and exist into view.
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) element.scrollIntoView();
    }
  }, [hash]);

  useEffect(() => {
    if (window.location.pathname === PATH_TERMS_OF_SERVICE) {
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.PAGE_LOADED,
      });
    }
  }, [window.location.pathname]);

  const renderTerms = (path: string) => {
    const content = getBodyContent(path);
    if (!content) {
      if (path === PATH_TERMS_LUX_Q2_VR_OFFER) {
        return (
          <Box className="terms-skeleton">
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton
              variant="text"
              width="40%"
              height={24}
              style={{ marginTop: 8 }}
            />
            <Skeleton
              variant="rect"
              width="100%"
              height={200}
              style={{ marginTop: 24, borderRadius: 4 }}
            />
          </Box>
        );
      }
      return null;
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <ContentBlock {...content} />;
  };

  return (
    <Box
      className={clsx("terms-body-container", {
        section:
          window.location.pathname !== PATH_TERMS_OF_SERVICE &&
          window.location.pathname !== PATH_SITE_TERMS_OF_USE,
      })}
    >
      <Switch>
        <Route
          path={[
            PATH_PRICE_PREDICTION_PRICE_WATCH,
            PATH_PRICE_DROP_PROTECTION,
            PATH_MISSED_CONNECTION_GUARANTEE,
            PATH_HOTEL_PRICE_DROP,
            PATH_CANCEL_FOR_ANY_REASON,
            PATH_CHANGE_FOR_ANY_REASON,
            PATH_HOTEL_CANCEL_FOR_ANY_REASON,
            PATH_DISRUPTION_PROTECTION,
            PATH_BEST_PRICE_GUARANTEE,
            PATH_TERMS_COMPLIMENTARY_NIGHT,
            PATH_TERMS_LUXURY_OFFER,
            PATH_TERMS_LUX_Q2_VR_OFFER,
            PATH_SITE_TERMS_OF_USE,
            PATH_TERMS_OF_SERVICE,
            PATH_TRAVEL_CREDITS,
            PATH_HOME,
            ...(isTravelWalletOfferExperiment ? [PATH_TRAVEL_OFFERS] : []),
            ...(isPremierCollectionExperiment ? [PATH_PREMIER_COLLECTION] : []),
          ]}
          render={(props) => renderTerms(props.location.pathname)}
          exact
        />
      </Switch>
    </Box>
  );
};

export default Body;
